import styled from "styled-components";
import { motion } from "framer-motion";
import { getToneTokens } from "../../styles/shared";
import { mq } from "../../styles/canonical/breakpoints";

export const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  justify-content: flex-end;
  background: rgba(10, 15, 25, 0.32);
`;

export const Panel = styled(motion.aside)`
  position: relative;
  width: min(480px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface};
  border-left: 1px solid ${({ theme }) => theme.border};
  box-shadow: -16px 0 40px rgba(0, 0, 0, 0.22);
  overflow: hidden;

  @media ${mq.tablet} {
    width: 100vw;
    border-left: 0;
  }
`;

export const Header = styled.header`
  /* PWA iOS: drawer full-height cobre a faixa da Dynamic Island. Mesma
     correcao do SlideOverPanel (2026-08-15) — sem isto o titulo e o X
     ficam atras do relogio/bateria. Fora do PWA a variavel e 0px. */
  padding: calc(16px + var(--safe-area-top, 0px)) 18px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  @media ${mq.tablet} {
    padding: calc(12px + var(--safe-area-top, 0px)) 16px 12px;
  }
`;

export const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const Eyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const CloseButton = styled.button`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.textPrimary};
  }

  @media ${mq.tablet} {
    width: var(--tap-min, 44px);
    height: var(--tap-min, 44px);
  }
`;

export const Titulo = styled.h2`
  margin: 8px 0 2px;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.1;
`;

export const Subtitulo = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const StatusPill = styled.span`
  ${({ theme, $tone = "default" }) => {
    const t = getToneTokens(theme, $tone);
    return `background:${t.background};border:1px solid ${t.border};color:${t.text};`;
  }}
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const Body = styled.div`
  padding: 14px 18px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 auto;

  @media ${mq.tablet} {
    padding: 12px 16px 20px;
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

export const InfoCard = styled.div`
  background: var(--surface-2);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  padding: 10px 12px;
  min-width: 0;
`;

export const InfoTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

export const InfoLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const InfoValor = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

export const InfoSub = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 500;
  color: ${({ theme, $tone }) =>
    $tone ? getToneTokens(theme, $tone).text : theme.textSecondary};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const EyeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textTertiary};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }

  @media ${mq.tablet} {
    min-width: 36px;
    min-height: 36px;
  }
`;

export const Avatar = styled.span`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: var(--accent-soft);
  color: var(--accent);
  border: 1px solid color-mix(in srgb, var(--accent) 24%, transparent);

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const RecebChips = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

export const RecebChip = styled.span`
  ${({ theme, $tone = "neutral" }) => {
    const t = getToneTokens(theme, $tone);
    return `background:${t.background};border:1px solid ${t.border};color:${t.text};`;
  }}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

/* ============ TIMELINE ============ */

export const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary};
  margin-bottom: 2px;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const TL = styled.ol`
  list-style: none;
  margin: 0;
  padding: 2px 0 0 2px;
`;

export const TLItem = styled(motion.li)`
  position: relative;
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 12px;
  padding: 4px 0 12px;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 12px;
    top: 26px;
    bottom: 0;
    width: 2px;
    background: ${({ theme, $status }) =>
      $status === "concluida" ? "var(--success-text)" : theme.border};
    border-radius: 2px;
  }
`;

export const TLDot = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  z-index: 1;
  ${({ theme, $status }) => {
    if ($status === "concluida")
      return `background: var(--success-text); color: var(--ink-on-accent); border: 2px solid var(--success-text);`;
    if ($status === "atual")
      return `background: var(--accent); color: var(--ink-on-accent); border: 2px solid var(--accent); box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 18%, transparent);`;
    return `background: ${theme.surface}; color: ${theme.textTertiary}; border: 2px solid ${theme.border};`;
  }}
`;

export const TLContent = styled.div`
  min-width: 0;
`;

export const TLHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const TLTitulo = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme, $status }) =>
    $status === "atual"
      ? "var(--accent)"
      : $status === "futura"
        ? theme.textSecondary
        : theme.textPrimary};
`;

export const TLData = styled.span`
  display: block;
  margin-top: 1px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme, $status }) =>
    $status === "concluida"
      ? "var(--success-text)"
      : $status === "atual"
        ? "var(--accent)"
        : theme.textTertiary};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const TLDesc = styled.div`
  margin-top: 1px;
  font-size: 11.5px;
  line-height: 1.4;
  color: ${({ theme }) => theme.textSecondary};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

export const TLBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: var(--accent-soft);
  color: var(--accent);

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;
